// Task grouping/sort (AMB-366 / D1, module 8).
//
// My Tasks groups by source meeting, latest meeting first; within a meeting, by due date
// (soonest first, undated last). Tasks with no source meeting fall into a trailing group.
// Pure — the screen just renders the groups it returns.

export interface GroupableTask {
  meetingId: string | null;
  meetingName?: string;
  meetingDate?: string | null;
  dueIso?: string | null;
}

export interface MeetingGroup<T extends GroupableTask> {
  meetingId: string | null;
  meetingName: string;
  meetingDate: string | null;
  items: T[];
}

// Soonest due first; undated tasks sort last.
function byDueAsc(a: GroupableTask, b: GroupableTask): number {
  if (a.dueIso && b.dueIso) return a.dueIso.localeCompare(b.dueIso);
  if (a.dueIso) return -1;
  if (b.dueIso) return 1;
  return 0;
}

export function groupTasksByMeeting<T extends GroupableTask>(tasks: T[]): MeetingGroup<T>[] {
  const groups = new Map<string, MeetingGroup<T>>();
  for (const task of tasks) {
    const key = task.meetingId ?? " none"; // sentinel so a null meeting groups together
    let g = groups.get(key);
    if (!g) {
      g = {
        meetingId: task.meetingId ?? null,
        meetingName: task.meetingName || (task.meetingId ? task.meetingId : "No meeting"),
        meetingDate: task.meetingDate ?? null,
        items: [],
      };
      groups.set(key, g);
    }
    g.items.push(task);
  }

  const ordered = [...groups.values()];
  ordered.sort((a, b) => {
    // No-meeting group always trails.
    if (a.meetingId === null && b.meetingId !== null) return 1;
    if (b.meetingId === null && a.meetingId !== null) return -1;
    // Latest meeting first; meetings with no date trail dated ones.
    if (a.meetingDate && b.meetingDate) return b.meetingDate.localeCompare(a.meetingDate);
    if (a.meetingDate) return -1;
    if (b.meetingDate) return 1;
    return 0;
  });
  for (const g of ordered) g.items.sort(byDueAsc);
  return ordered;
}
